<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;

const props = defineProps<{
	allowedToEdit?: boolean;
	selectedCurveId?: number;
	hint?: string;
}>();

const emit = defineEmits<{
	(e: "select", curve: PriceModelCurveDO): void;
}>();

const api = useApiClient("bookingExperts");
const isLoading = ref(false);
const curves = ref<PriceModelCurveDO[]>([]);
const selected = ref<number | null>(props.selectedCurveId ?? null);

async function loadCurves() {
	if (curves.value.length > 0) return;
	isLoading.value = true;
	try {
		const result = await toastApiResult(api.getPriceModelCurves());
		if (result.isOk()) {
			curves.value = result.value;
		}
	} finally {
		isLoading.value = false;
	}
}

function onSelect(curveId: number) {
	const curve = curves.value.find((c) => c.id === curveId);
	if (curve) {
		emit("select", curve);
	}
}

onMounted(() => loadCurves());
</script>

<template>
	<v-alert
		v-if="allowedToEdit !== undefined && !allowedToEdit"
		border="start"
		density="compact"
		color="error"
		variant="tonal"
	>
		Can Not Edit The Past
	</v-alert>
	<v-autocomplete
		v-model="selected"
		:loading="isLoading"
		:items="curves"
		item-title="name"
		item-value="id"
		label="Select Curve"
		:hide-details="!hint"
		:persistent-hint="hint !== undefined"
		:hint="hint"
		:disabled="!allowedToEdit"
		@update:model-value="onSelect"
	>
		<template #item="{ props, item }">
			<v-list-item v-bind="props" title="">
				<template #prepend>
					<div class="chit mr-2" :style="{ backgroundColor: item.raw.color }" />
				</template>
				<v-list-item-title>{{ item.raw.name }}</v-list-item-title>
				<v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
			</v-list-item>
		</template>

		<template #selection="{ item }">
			<div class="d-flex align-center">
				<div class="chit mr-2" :style="{ backgroundColor: item.raw.color }" />
				{{ item.raw.name }}
			</div>
		</template>
	</v-autocomplete>
</template>

<style scoped>
:global(.chit) {
	width: 15px;
	height: 15px;
	background: #f8f8f8;
	border-radius: 10px;
	margin-right: 8px;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5),
		inset 0 -1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
